// c1-rate-coverage-checkpoint (brief 30 §3, 2026-08-04) — rendering the
// interest engine's refusals.
//
// THE DEFECT. `computeAmount` returns an English sentence with ISO dates and a
// raw kind key, and it reaches a Belgian lawyer verbatim in two places: the
// generated .docx (`docGen.js` errorMsg) and the clipboard prose export. What
// they read was
//
//   commercial_interest has no rate for 2026-07-09 to 2026-08-04 — the
//   official-rate table does not cover these dates (a missing rate row, not a
//   bad input)
//
// inside a document whose every other line says «Intérêts commerciaux
// (semestriels)» and «09/07/2026». Three faults in one sentence — the raw kind
// key, the ISO dates, the language — and not a new class: `principal must be
// > 0` and `startDate required` have reached that same line in English since
// Phase 15. The rate gap merely made the path common enough to see.
//
// THE SHAPE CHOSEN, of brief 30 §3's three options: (1) the engine returns a
// STRUCTURED refusal and each surface renders it in its own language.
//   · (2) — the engine builds a French string — puts a language inside
//     `'use strict'` server code with no translator, and is wrong the day the
//     calculator UI is read in Dutch. The UI is one of the four consumers.
//   · (3) — the memo assembler re-renders from a code — fixes one surface of
//     four and leaves the UI and the clipboard on the English string, which is
//     option 1 with three quarters of the work left undone.
// Only (1) also fixes the clipboard export and the UI, which is what makes it
// worth the recording re-freeze it costs.
//
// TWO RENDERERS, because there are two audiences and the 2026-07-12 B.5 ruling
// separates them: text inside a GENERATED DOCUMENT follows the document
// language (French, always — the memo and the clipboard export), and UI chrome
// follows the reader's locale (`t()`). `refusalFR` serves the first,
// `refusalUI` the second.
//
// `server/src/lib/docGen.js` deliberately carries its OWN French renderer
// rather than importing this one: it and `memoExport.ts` are two independently
// written implementations of the same document, and `recordings/p17-9-interest-memo`
// exists to make them agree paragraph for paragraph. Collapsing them would
// turn that cross-check into a restatement.
//
// BOTH FALL BACK TO THE ENGLISH `error`. A refusal shape neither knows must
// print something rather than nothing — this text lands in a document.

export type RefusalRange = { from: string; to: string };

/** The engine's `errorInfo` — the FACTS of a refusal, without its words. */
export type RefusalInfo =
  | {
      code?: string;
      kind?: string;
      ranges?: RefusalRange[];
      refused?: number;
      total?: number;
    }
  | null
  | undefined;

/** A rateType → its label, in whatever language the calling surface wants. */
export type RateLabel = (kind: string | undefined) => string;

/**
 * `t` from useTranslation. Declared here rather than taken as `any` because
 * these renderers are the reason the keys exist; `vars` carries the
 * interpolation values plus the `fallback` the resolver honours on a miss.
 */
export type Translate = (key: string, vars?: Record<string, unknown>) => string;

// dd/mm/yyyy, the convention every other date in the memo and the calculator
// already uses (fmtBEDate / memoBEDate).
function beDate(iso: string | Date | null | undefined) {
  if (!iso) return "—";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getUTCFullYear()}`;
}

// The French words for the engine's rate types. Byte-identical to
// `docGen.js`'s MEMO_RATE_LABELS and to `translations_seed.json`'s
// `case.calculator.rate_type_*` / fr rows — verified, not assumed, because the
// p17-9 recording compares the two documents paragraph for paragraph and a
// one-character divergence here would surface as a memo regression.
// An unknown rateType echoes back raw, as every other resolver in this family
// does.
const RATE_LABELS_FR: Record<string, string> = {
  legal_interest: "Intérêts légaux (annuels)",
  commercial_interest: "Intérêts commerciaux (semestriels)",
};

function frRanges(ranges: RefusalRange[] | undefined) {
  return (Array.isArray(ranges) ? ranges : [])
    .map((r) => `du ${beDate(r.from)} au ${beDate(r.to)}`)
    .join(", ");
}

/**
 * The refusal as it belongs in a French document (the .docx, the clipboard).
 *
 * @param info      the engine's `errorInfo`
 * @param error     the engine's English `error`, used when the code is unknown
 * @param rateLabel optional resolver for a rateType → its label. `memoExport`
 *                  passes its own so the p17-9 oracle's translator keeps
 *                  deciding the memo's words; everyone else takes the French
 *                  default below, which is what a French document wants.
 */
export function refusalFR(info: RefusalInfo, error?: string, rateLabel?: RateLabel): string {
  const label = (k: string | undefined) =>
    rateLabel ? rateLabel(k) : RATE_LABELS_FR[String(k)] || String(k ?? "");
  switch (info && info.code) {
    case "rate_gap":
      return (
        `${label(info!.kind)} : aucun taux officiel ${frRanges(info!.ranges)} — la table des ` +
        `taux officiels ne couvre pas ces dates (il manque une ligne de taux ; ce n'est pas une donnée erronée)`
      );
    case "no_rates":
      return `${label(info!.kind)} : aucun taux officiel n'est enregistré pour ce type`;
    case "principal_required":
      return "le principal doit être supérieur à zéro";
    case "start_date_required":
      return "la date de départ est requise";
    case "missing_amount":
      return "montant manquant";
    case "amounts_refused":
      return (
        `${info!.refused} montant(s) sur ${info!.total} n'ont pas pu être calculés — ` +
        `aucun total ne peut être établi`
      );
    default:
      return String(error || "calcul indisponible");
  }
}

/**
 * The refusal as UI chrome, in the reader's language.
 *
 * Every key ships in en/fr/nl (scripts/lint/i18n-keys-complete.js) and the
 * fallbacks are the English the engine would otherwise have shown, so a box
 * whose translations have not been seeded is no worse off than before.
 */
export function refusalUI(info: RefusalInfo, error: string | undefined, t: Translate, rateLabel?: RateLabel): string {
  const label = (k: string | undefined) => (rateLabel ? rateLabel(k) : String(k ?? ""));
  const beRanges = (ranges: RefusalRange[] | undefined) =>
    (Array.isArray(ranges) ? ranges : []).map((r) => `${beDate(r.from)} → ${beDate(r.to)}`).join(", ");
  switch (info && info.code) {
    case "rate_gap":
      return t("case.calculator.refusal.rate_gap", {
        kind: label(info!.kind),
        ranges: beRanges(info!.ranges),
        fallback:
          "{kind}: no official rate for {ranges} — the rate table does not cover these dates (a missing rate row, not a bad input)",
      });
    case "no_rates":
      return t("case.calculator.refusal.no_rates", {
        kind: label(info!.kind),
        fallback: "{kind}: no official rate is recorded for this type",
      });
    case "principal_required":
      return t("case.calculator.refusal.principal_required", {
        fallback: "the principal must be greater than zero",
      });
    case "start_date_required":
      return t("case.calculator.refusal.start_date_required", {
        fallback: "a start date is required",
      });
    case "missing_amount":
      return t("case.calculator.refusal.missing_amount", { fallback: "amount missing" });
    case "amounts_refused": {
      const { refused, total } = info!; // the refusal carries both
      return t("case.calculator.refusal.amounts_refused", {
        refused,
        total,
        count: refused, // 208 §20 — the refused amounts pick the form
        fallback: "{refused} of {total} amounts could not be computed — no total can be stated",
      });
    }
    default:
      return String(error || t("case.calculator.refusal.unavailable", { fallback: "calculation unavailable" }));
  }
}
